import { CITY_GUIDE_IMAGE_SLOTS } from "./city-page.js";
import { CITY_GUIDE_CATEGORY_LIMIT, CITY_PAGE_PREVIEW_COUNT, } from "./landing.js";
/**
 * The closed set of section types a city guide is composed of, and the one
 * description of what each of them is.
 *
 * A guide is `template ⊕ city deltas ⊕ live data`: this module holds the
 * template's own vocabulary (what a section is called, what it reads, what an
 * author may change about it, and what it renders when), and the resolver beside
 * it turns a template, one city's deltas and the rendered payload into the
 * ordered list the landing page renders. The backend payload builder, the
 * landing renderer and the dashboard studio all read this file and read it for
 * the same facts: order, headings and visibility rules are never re-stated per
 * caller, and a section that cannot be controlled is a gap to fill here rather
 * than a block to special-case somewhere else.
 *
 * The set is closed on purpose. A section's internals stay code-owned and only
 * its named slots below are authorable, so adding a section is one entry here
 * plus one renderer component — never a free-form page builder.
 *
 * This half carries no schema library: the guide's client bundle imports it, and
 * the settings schemas (zod) live beside it in `city-page-section-props.ts`,
 * which only the backend and the dashboard load.
 */
/** The section types, in the order the guide has always rendered them. */
export const SECTION_TYPE_IDS = [
    "identity",
    "instagram",
    "contents",
    "history",
    "experiences",
    "going-out",
    "essentials",
    "cost",
    "venues",
    "contact",
];
/**
 * The identity header's cells, in the order it draws them. This is the render
 * order, not just a list: an author's selection is drawn in the sequence given,
 * and the default selection is therefore the header as it renders today.
 */
export const IDENTITY_CELLS = ["weather", "flights", "money", "clock"];
/** The identity header's default cell selection, in render order. */
export const DEFAULT_IDENTITY_CELLS = IDENTITY_CELLS;
/** Places the experiences section shows by default; also its ceiling. */
export const CITY_GUIDE_EXPERIENCE_LIMIT = 5;
/** Categories the going-out section shows by default; also its ceiling. */
export const CITY_GUIDE_GOING_OUT_LIMIT = 3;
/** Ceiling for the venue directory, which renders every row until an author caps it. */
export const CITY_GUIDE_VENUE_LIMIT = 100;
/**
 * The code-owned heading of every section that prints one. The guide's own copy,
 * read from the JSX it replaces; the identity block's line is the city's name and
 * the contents strip's entries are the sections, so neither has an entry here.
 */
export const DEFAULT_HEADINGS = {
    instagram: "LATEST ON INSTAGRAM",
    history: "A LITTLE HISTORY",
    experiences: "UNIQUE EXPERIENCES",
    "going-out": "GOING OUT",
    essentials: "ESSENTIALS",
    cost: "WHAT THINGS COST",
    venues: "FIND IT",
    contact: "GET IN TOUCH",
};
const HEADED_SLOTS = ["heading"];
/** Every section type, keyed by id. */
export const SECTION_TYPES = {
    identity: {
        id: "identity",
        label: "Identity header",
        binding: {
            reads: "the city row and the city's feed readings (weather, currency, flights, clock)",
            freshness: "read at render time; refreshed by the hourly sweep",
            owners: [
                { label: "Weather", href: "/weather" },
                { label: "Currency", href: "/currency" },
            ],
        },
        textSlots: [],
        mediaSlots: [],
        icon: false,
        numbered: false,
        pinned: "first",
    },
    instagram: {
        id: "instagram",
        label: "Latest on Instagram",
        binding: {
            reads: "the city's own latest archived Instagram posts and its handle",
            freshness: "the latest posts, refreshed by the archive sync",
            owners: [{ label: "Cities", href: "/settings/cities" }],
        },
        textSlots: HEADED_SLOTS,
        mediaSlots: [],
        icon: true,
        numbered: false,
        pinned: "first",
    },
    contents: {
        id: "contents",
        label: "Contents",
        binding: {
            reads: "the sections rendered above it",
            freshness: "derived at render time",
            owners: [],
        },
        textSlots: [],
        mediaSlots: [],
        icon: false,
        numbered: false,
        pinned: "first",
        locked: true,
        derived: true,
    },
    history: {
        id: "history",
        label: "A little history",
        binding: {
            reads: "the authored history text and its illustration",
            freshness: "authored",
            owners: [],
        },
        textSlots: HEADED_SLOTS,
        mediaSlots: CITY_GUIDE_IMAGE_SLOTS,
        icon: true,
        numbered: true,
    },
    experiences: {
        id: "experiences",
        label: "Unique experiences",
        binding: {
            reads: "the city's active attraction pool, in the order it was curated",
            freshness: "read at render time",
            owners: [
                {
                    label: "Attractions",
                    href: "/settings/cities?cityView=information&informationView=attractions",
                    needsCity: true,
                },
            ],
        },
        textSlots: HEADED_SLOTS,
        mediaSlots: [],
        icon: true,
        numbered: true,
    },
    "going-out": {
        id: "going-out",
        label: "Going out",
        binding: {
            reads: "90 days of the city's approved events, grouped by category",
            freshness: "read at render time",
            owners: [{ label: "Categories", href: "/settings/categories" }],
        },
        textSlots: HEADED_SLOTS,
        mediaSlots: [],
        icon: true,
        numbered: true,
    },
    essentials: {
        id: "essentials",
        label: "Essentials",
        binding: {
            reads: "the authored banks, emergency numbers, transport and food notes",
            freshness: "authored",
            owners: [],
        },
        textSlots: HEADED_SLOTS,
        mediaSlots: [],
        icon: true,
        numbered: true,
    },
    cost: {
        id: "cost",
        label: "What things cost",
        binding: {
            reads: "the city's scraped cost-of-living reading",
            freshness: "the feed row, refreshed by the feed sync",
            owners: [{ label: "Cost of Living", href: "/cost-of-living" }],
        },
        textSlots: HEADED_SLOTS,
        mediaSlots: [],
        icon: true,
        numbered: true,
    },
    venues: {
        id: "venues",
        label: "Find it",
        binding: {
            reads: "the city's verified venues",
            freshness: "read at render time",
            owners: [{ label: "Locations", href: "/locations" }],
        },
        textSlots: HEADED_SLOTS,
        mediaSlots: [],
        icon: true,
        numbered: true,
    },
    contact: {
        id: "contact",
        label: "Get in touch",
        binding: {
            reads: "the guide's own contact form",
            freshness: "always rendered, always last",
            owners: [],
        },
        textSlots: HEADED_SLOTS,
        mediaSlots: [],
        icon: false,
        numbered: true,
        pinned: "last",
        locked: true,
    },
};
/** The composition that reproduces the guide as it renders today. */
export const DEFAULT_TEMPLATE_SECTIONS = SECTION_TYPE_IDS;
/** True when a value is one of the closed set of section types. */
export function isSectionTypeId(value) {
    return typeof value === "string" && SECTION_TYPE_IDS.includes(value);
}
/**
 * The anchor a section is addressable by: `history` -> `a-little-history`.
 *
 * The id comes from the registry's own heading, never from the rendered one, so
 * an author overriding a heading cannot move the anchor the contents strip and
 * every existing deep link point at.
 */
export function sectionAnchor(id) {
    const heading = DEFAULT_HEADINGS[id] ?? id;
    return heading
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "");
}
/** Category slugs that name a bucket rather than a kind of event. */
const GENERIC_EVENT_TYPE_SLUGS = new Set(["misc", "other"]);
/**
 * The city's top named categories, in the backend's own order (count
 * descending), with the generic buckets dropped and the tail trimmed to
 * `CITY_GUIDE_CATEGORY_LIMIT`.
 *
 * GOING OUT describes the first `limit` of these and prints nothing else from
 * the list, and a city with none of them renders no section at all — so the
 * section's own visibility rule below reads this same call rather than a second
 * rule that could disagree with what the reader sees.
 */
export function guideCategoryChips(mix) {
    if (!mix)
        return [];
    return mix.categories
        .filter((share) => !GENERIC_EVENT_TYPE_SLUGS.has(share.slug))
        .slice(0, CITY_GUIDE_CATEGORY_LIMIT);
}
/**
 * Every section's settings as they read when a city says nothing, so a guide can
 * be composed without loading the settings schemas at all — the landing's
 * fallback path. `resolveSections({})` must agree with this exactly; a shared
 * test holds the two together, because this is the one place a default is
 * written twice.
 */
const DEFAULT_SETTINGS = {
    identity: { cells: [...IDENTITY_CELLS] },
    instagram: { limit: CITY_PAGE_PREVIEW_COUNT },
    contents: {},
    history: {},
    experiences: { limit: CITY_GUIDE_EXPERIENCE_LIMIT },
    "going-out": { limit: CITY_GUIDE_GOING_OUT_LIMIT },
    essentials: {},
    cost: {},
    venues: {},
    contact: {},
};
/**
 * The guide as it renders with no composition anywhere: every section, in the
 * registry's order, with the registry's headings and defaults. This is what a
 * payload that predates the composition layer describes, and what a database with
 * no template row renders.
 */
export const DEFAULT_RESOLVED_SECTIONS = DEFAULT_TEMPLATE_SECTIONS.map((id) => ({
    id,
    enabled: true,
    heading: DEFAULT_HEADINGS[id] ?? "",
    ...DEFAULT_SETTINGS[id],
}));
/**
 * Whether each section has something to draw — the guide's own rules, in one
 * table.
 *
 * These are the conditions the renderer used to apply one `push` at a time, and
 * the payload builder and the fallback both read them here: a section cannot be
 * visible on the page and missing from the payload's own account of itself.
 */
export const SECTION_VISIBILITY = {
    // The header, the contents strip and the contact form are the guide's
    // furniture: they render whether or not the city has anything else.
    identity: () => true,
    contents: () => true,
    contact: () => true,
    instagram: (data) => Boolean(data.instagramHandle) && (data.instagramPosts ?? []).length > 0,
    history: (data) => Boolean(data.history?.trim()),
    experiences: (data) => (data.attractions ?? []).length > 0,
    "going-out": (data) => guideCategoryChips(data.categoryMix).length > 0,
    essentials: (data) => {
        const facts = data.essentialInfo;
        if (facts?.emergency)
            return true;
        return ["banks", "gettingAround", "foodDrink", "mustTry"].some((key) => facts?.[key]?.trim() !== "");
    },
    cost: (data) => Boolean(data.live?.costOfLiving),
    venues: (data) => data.venues.length > 0,
};
/**
 * The guide's sections as the payload carries them: resolved order, the settings
 * that travel with them, and whether the document draws each one — with the
 * reason when it does not. An author's `enabled: false` wins over a section whose
 * data is there, so the answer says what will actually be drawn.
 */
export function resolvedGuideSections(sections, data) {
    return sections.map((section) => {
        const rendered = section.enabled && SECTION_VISIBILITY[section.id](data);
        return {
            id: section.id,
            heading: section.heading,
            ...(section.icon !== undefined ? { icon: section.icon } : {}),
            ...(section.limit !== undefined ? { limit: section.limit } : {}),
            ...(section.cells !== undefined ? { cells: section.cells } : {}),
            rendered,
            ...(rendered ? {} : { hiddenReason: section.enabled ? "no-data" : "disabled" }),
        };
    });
}
//# sourceMappingURL=city-page-sections.js.map